import { useEffect, useRef, useState } from 'react';
import { requiresSafariHack, supportsHiddenUntilFound } from '../../../helper';
import { useBeforeMatch } from '../../../hooks/useBeforeMatch';
import styles from './useReadMoreVisibility.module.scss';
export const useReadMoreVisibility = ({ contentRef, linkRef, isOpen, autoScrollToContent = true, rows = 0, }) => {
    const firstUpdate = useRef(true);
    const scrollHeight = useRef(0);
    const [isVisible, setIsVisible] = useState(isOpen);
    useEffect(() => {
        const setInitialStyles = () => {
            // eslint-disable-next-line @typescript-eslint/no-shadow
            const contentElem = contentRef?.current;
            const linkElem = linkRef?.current;
            if (!contentElem || !linkElem)
                return;
            if (rows === 0 && !supportsHiddenUntilFound) {
                contentElem.classList.add(styles.old);
            }
            const lineHeight = Number.parseInt(getComputedStyle(contentElem).lineHeight, 10);
            const rowsHeight = rows * lineHeight;
            const shouldHideLink = rowsHeight >= contentElem.scrollHeight;
            if (isVisible) {
                contentElem.style.height = 'auto';
                contentElem.style.setProperty('--line-number', 'none');
            }
            else {
                linkElem.style.display = shouldHideLink ? 'none' : 'block';
                contentElem.style.height = shouldHideLink
                    ? `${contentElem.scrollHeight}px`
                    : `${rowsHeight}px`;
                contentElem.style.setProperty('--line-number', `${rows}`);
                // @ts-expect-error -- TS doesn't know `until-found` yet
                contentElem.hidden = shouldHideLink || requiresSafariHack ? false : 'until-found';
            }
        };
        const scrollToElement = (element) => {
            if (element.scrollIntoViewIfNeeded)
                element.scrollIntoViewIfNeeded({ behavior: 'smooth' });
            else
                element.scrollIntoView({ behavior: 'smooth' });
        };
        const handleTransitionEnd = () => {
            // eslint-disable-next-line @typescript-eslint/no-shadow
            const contentElem = contentRef?.current;
            if (!contentElem)
                return;
            contentElem.ontransitionend = null;
            if (isVisible) {
                contentElem.style.height = 'auto';
                return;
            }
            contentElem.style.setProperty('--line-number', `${rows}`);
            // @ts-expect-error -- TS doesn't know `until-found` yet
            contentElem.hidden = requiresSafariHack ? false : 'until-found';
            if (autoScrollToContent) {
                scrollToElement(contentElem);
            }
        };
        const contentElem = contentRef?.current;
        if (!contentElem)
            return undefined;
        let afterAnimationTimeout;
        if (firstUpdate.current) {
            setInitialStyles();
            firstUpdate.current = false;
        }
        else {
            if (contentElem.scrollHeight !== 0 &&
                contentElem.scrollHeight !== scrollHeight.current) {
                scrollHeight.current = contentElem.scrollHeight;
            }
            const rowsHeight = rows * Number.parseInt(getComputedStyle(contentElem).lineHeight, 10);
            const initHeight = isVisible ? rowsHeight : scrollHeight.current;
            const endHeight = isVisible ? scrollHeight.current : rowsHeight;
            contentElem.ontransitionend = null;
            contentElem.removeAttribute('hidden');
            contentElem.style.height = `${initHeight}px`;
            contentElem.style.setProperty('--line-number', 'none');
            // fallback in case the animation is not run for various reasons (e.g. "prefers reduced motion" enabled in OS, battery saving turn on, ...)
            afterAnimationTimeout = setTimeout(handleTransitionEnd, 420); // animations runs 400 ms, so 420 blaze it
            if (initHeight !== endHeight) {
                contentElem.ontransitionend = e => {
                    if (e.propertyName === 'height') {
                        clearTimeout(afterAnimationTimeout);
                        handleTransitionEnd();
                    }
                };
                // eslint-disable-next-line @typescript-eslint/no-unused-expressions -- force "Style" re-calc, so that height can be updated
                getComputedStyle(contentElem).opacity;
                contentElem.style.height = `${endHeight}px`;
            }
        }
        return () => {
            clearTimeout(afterAnimationTimeout);
            contentElem.ontransitionend = null;
        };
    }, [autoScrollToContent, contentRef, isVisible, linkRef, rows]);
    useBeforeMatch({
        contentRef,
        onMatch: () => {
            setIsVisible(true);
        },
    });
    const handleToggleVisibility = () => {
        setIsVisible(visible => !visible);
    };
    return {
        isVisible,
        toggleVisibility: handleToggleVisibility,
    };
};
